// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  FileDownloadOutlined,
  FileUploadOutlined,
  CenterFocusStrong,
  FitScreen,
  ErrorOutline,
} from '@mui/icons-material';
import { IconButton, Stack, Tabs, Tab, Box, TextField, Autocomplete } from '@mui/material';
import { FadingTooltip } from '@cosmotech/ui';
import { ModuleSelector } from './ModuleSelector';

export const Toolbar = ({
  modules,
  onModuleCreated,
  onModuleSelectionChange,
  onModuleDeleted,
  onGraphDownload,
  onGraphUpload,
  tabTitles,
  activeTab,
  onTabChange,
  onZoomToFitFullMap,
  onZoomOnHighlightedElements,
  readOnly = false,
  cells,
  transports,
  onCellSearchSelect,
  onTransportSearchSelect,
  onOpenErrorsDialog,
}) => {
  const { t } = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [selectedOption, setSelectedOption] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState('');

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(!!fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const normalizedCells = useMemo(
    () =>
      (cells || []).map((c) =>
        typeof c === 'string'
          ? { id: c, name: c }
          : {
              id: c.id || c.name,
              name: c.name,
            }
      ),
    [cells]
  );

  const normalizedTransports = useMemo(
    () =>
      (transports || []).map((t) => ({
        id: t.id || t.name,
        name: t.name,
        sourceName: t.sourceName,
        destinationName: t.destinationName,
      })),
    [transports]
  );

  const combinedOptions = useMemo(
    () => [
      ...normalizedCells.map((c) => ({ ...c, __kind: 'cell' })),
      ...normalizedTransports.map((tr) => ({ ...tr, __kind: 'transport' })),
    ],
    [normalizedCells, normalizedTransports]
  );

  const errorsButton = useMemo(() => {
    if (!onOpenErrorsDialog) return null;
    const tooltipText = t(
      'customcomponents.interactiveMap.toolbar.buttons.errors',
      'Afficher les erreurs de configuration'
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <FadingTooltip title={tooltipText} disableInteractive={true}>
          <span>
            <IconButton
              onClick={onOpenErrorsDialog}
              data-cy="open-errors-dialog"
              className={isFullscreen ? 'fullscreen-tooltip' : ''}
              {...(isFullscreen && { 'data-tooltip': tooltipText })}
            >
              <ErrorOutline color="error" />
            </IconButton>
          </span>
        </FadingTooltip>
      </Box>
    );
  }, [t, onOpenErrorsDialog, isFullscreen]);

  const graphDownloadButton = useMemo(() => {
    if (!onGraphDownload) return null;
    const tooltipText = t('customcomponents.interactiveMap.toolbar.buttons.download', 'Télécharger');

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <FadingTooltip title={tooltipText} disableInteractive={true}>
          <IconButton
            onClick={onGraphDownload}
            data-cy="download-button"
            className={isFullscreen ? 'fullscreen-tooltip' : ''}
            {...(isFullscreen && { 'data-tooltip': tooltipText })}
          >
            <FileDownloadOutlined />
          </IconButton>
        </FadingTooltip>
      </Box>
    );
  }, [onGraphDownload, t, isFullscreen]);

  const graphUploadButton = useMemo(() => {
    if (!onGraphUpload || readOnly) return null;
    const tooltipText = t('customcomponents.interactiveMap.toolbar.buttons.upload', 'Charger');

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <FadingTooltip title={tooltipText} disableInteractive={true}>
          <IconButton
            onClick={onGraphUpload}
            data-cy="upload-button"
            className={isFullscreen ? 'fullscreen-tooltip' : ''}
            {...(isFullscreen && { 'data-tooltip': tooltipText })}
          >
            <FileUploadOutlined />
          </IconButton>
        </FadingTooltip>
      </Box>
    );
  }, [onGraphUpload, readOnly, t, isFullscreen]);

  const zoomToFitFullMapButton = useMemo(() => {
    if (!onZoomToFitFullMap) return null;
    const tooltipText = t(
      'customcomponents.interactiveMap.toolbar.buttons.zoomToFitFullMap',
      'Zoom sur la carte entière'
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <FadingTooltip title={tooltipText} disableInteractive={true}>
          <IconButton
            onClick={onZoomToFitFullMap}
            data-cy="zoom-full-map-button"
            className={isFullscreen ? 'fullscreen-tooltip' : ''}
            {...(isFullscreen && { 'data-tooltip': tooltipText })}
          >
            <FitScreen />
          </IconButton>
        </FadingTooltip>
      </Box>
    );
  }, [onZoomToFitFullMap, t, isFullscreen]);

  const zoomOnHighlightedElementsButton = useMemo(() => {
    if (!onZoomOnHighlightedElements) return null;
    const tooltipText = t(
      'customcomponents.interactiveMap.toolbar.buttons.zoomOnHighlightedElements',
      'Zoom sur les éléments surlignés'
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <FadingTooltip title={tooltipText} disableInteractive={true}>
          <IconButton
            onClick={onZoomOnHighlightedElements}
            data-cy="zoom-highlighted-elements-button"
            className={isFullscreen ? 'fullscreen-tooltip' : ''}
            {...(isFullscreen && { 'data-tooltip': tooltipText })}
          >
            <CenterFocusStrong />
          </IconButton>
        </FadingTooltip>
      </Box>
    );
  }, [onZoomOnHighlightedElements, t, isFullscreen]);

  const handleTabChange = (event, newValue) => {
    onTabChange && onTabChange(newValue);
  };

  const handleSearchChange = (event, option) => {
    setSelectedOption(option);
    if (!option) {
      return;
    }

    if (option.__kind === 'cell') {
      onCellSearchSelect && onCellSearchSelect(option);
    } else if (option.__kind === 'transport') {
      onTransportSearchSelect && onTransportSearchSelect(option);
    }
  };

  const handleSearchInputChange = (event, newInputValue) => {
    setSearchInputValue(newInputValue);
  };

  const getOptionLabel = (option) => {
    if (!option) return '';
    if (option.__kind === 'transport') {
      const src = option.sourceName || '';
      const dst = option.destinationName || '';
      if (src || dst) {
        return `${option.name}`;
      }
    }
    return option.name || '';
  };

  const renderOption = (props, option) => {
    const isTransport = option.__kind === 'transport';
    const label = getOptionLabel(option);

    return (
      <li {...props} key={`${option.__kind}-${option.id || option.name}`}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ fontWeight: 500 }}>{label}</Box>
          <Box sx={{ fontSize: '0.75rem', opacity: 0.7 }}>
            {isTransport
              ? t('customcomponents.interactiveMap.toolbar.search.transportLabel', 'Transport')
              : t('customcomponents.interactiveMap.toolbar.search.cellLabel', 'Sous-fonction')}
          </Box>
        </Box>
      </li>
    );
  };

  const searchPlaceholder = t(
    'customcomponents.interactiveMap.toolbar.search.placeholder',
    'Rechercher une sous-fonction ou un transport…'
  );

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <ModuleSelector
          disabled={readOnly}
          modules={modules}
          onModuleCreated={onModuleCreated}
          onModuleSelectionChange={onModuleSelectionChange}
          onModuleDeleted={onModuleDeleted}
        />
        <Tabs
          value={activeTab || false}
          onChange={handleTabChange}
          aria-label="interactive-map-tabs"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40 },
          }}
        >
          {tabTitles.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Autocomplete
          size="small"
          sx={{ minWidth: 260 }}
          options={combinedOptions}
          getOptionLabel={getOptionLabel}
          renderOption={renderOption}
          value={selectedOption}
          inputValue={searchInputValue}
          onChange={handleSearchChange}
          onInputChange={handleSearchInputChange}
          renderInput={(params) => <TextField {...params} label={searchPlaceholder} variant="outlined" />}
          disablePortal
        />
        {errorsButton}
        {zoomToFitFullMapButton}
        {zoomOnHighlightedElementsButton}
        {graphUploadButton}
        {graphDownloadButton}
      </Stack>
    </Stack>
  );
};

Toolbar.propTypes = {
  modules: PropTypes.arrayOf(PropTypes.string),
  onModuleCreated: PropTypes.func,
  onModuleSelectionChange: PropTypes.func,
  onModuleDeleted: PropTypes.func,
  onGraphDownload: PropTypes.func,
  onGraphUpload: PropTypes.func,
  tabTitles: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  activeTab: PropTypes.any,
  onTabChange: PropTypes.func,
  onZoomToFitFullMap: PropTypes.func,
  onZoomOnHighlightedElements: PropTypes.func,
  readOnly: PropTypes.bool,
  cells: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string.isRequired,
      }),
    ])
  ),
  transports: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string.isRequired,
      sourceName: PropTypes.string,
      destinationName: PropTypes.string,
    })
  ),
  onCellSearchSelect: PropTypes.func,
  onTransportSearchSelect: PropTypes.func,
  onOpenErrorsDialog: PropTypes.func,
};
